import React from "react";
import { ArrowRight, Compass, Mail, Phone, MessageSquare, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHead, StatusPill } from "./acqUi";

export function Readiness({ items, text, progress, goTo }) {
  return (
    <section data-testid="acq-readiness-section">
      <SectionHead eyebrow="Where You Stand" title="Your Acquisition Readiness" text={text}
        right={<div className="text-right"><p className="font-display text-4xl font-bold text-[#0B2E4F]" data-testid="acq-progress-pct">{progress}%</p><p className="text-xs text-slate-500">required milestones verified</p></div>} />
      {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="acq-readiness-empty">Your district team hasn't set up your program requirements yet.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((r) => {
            const Tag = r.link ? "button" : "div";
            return (
              <Tag key={r.key} onClick={r.link ? () => goTo(r.link.tab, r.link.sub) : undefined} className={`text-left rounded-xl border bg-white p-4 ${r.status === "complete" ? "border-emerald-200" : "border-slate-200"} ${r.link ? "hover:border-[#006090]/50 hover:shadow-sm transition-[box-shadow,border-color]" : ""}`} data-testid={`acq-ready-${r.key}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                  {r.status === "complete" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${r.status === "complete" ? "bg-emerald-500" : r.status === "needs_attention" ? "bg-rose-500" : "bg-[#006090]"}`} style={{ width: `${Math.round((r.done / r.total) * 100)}%` }} /></div>
                <div className="mt-2"><StatusPill p={r} testId={`acq-ready-status-${r.key}`} /></div>
              </Tag>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function NextStep({ step, goTo }) {
  const district = step && step.owner !== "candidate";
  return (
    <section className="rounded-2xl bg-[#0B2E4F] text-white p-6 sm:p-8 relative overflow-hidden" data-testid="acq-next-step">
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full border-[28px] border-white/5" aria-hidden />
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-300 flex items-center gap-1.5"><Compass className="w-4 h-4" />Your Next Step</p>
      {!step ? (
        <p className="font-display text-2xl font-bold mt-2" data-testid="acq-next-step-title">You're all caught up — your district team will guide you to the next milestone.</p>
      ) : (
        <>
          <p className="font-display text-2xl sm:text-3xl font-bold mt-2 max-w-3xl" data-testid="acq-next-step-title">{step.title}</p>
          {(district || step.instructions) && <p className="text-sm text-white/80 mt-2 max-w-2xl flex items-start gap-1.5" data-testid="acq-next-step-text">{district && <Clock className="w-4 h-4 mt-0.5 shrink-0" />}{district ? "Waiting on your district team — no action is needed from you right now." : step.instructions}</p>}
          {step.link && <Button onClick={() => goTo(step.link.tab, step.link.sub)} className="mt-5 bg-white text-[#0B2E4F] hover:bg-sky-50" data-testid="acq-next-step-cta">Go to {step.category ? step.category.charAt(0).toUpperCase() + step.category.slice(1) : "My Checklist"} <ArrowRight className="w-4 h-4 ml-1.5" /></Button>}
        </>
      )}
    </section>
  );
}

export function DistrictTeam({ people, message, cta, onContact }) {
  return (
    <section data-testid="acq-support-section">
      <SectionHead eyebrow="We're Here to Help" title="Your District Team" text={message}
        right={<Button onClick={onContact} className="bg-[#006090] hover:bg-[#004e75] text-white" data-testid="acq-contact-team"><MessageSquare className="w-4 h-4 mr-1.5" />{cta}</Button>} />
      {people.length === 0 ? <p className="text-sm text-slate-500" data-testid="acq-support-empty">Your district team contacts will appear here once they're assigned.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {people.map((p, i) => (
            <div key={`${p.name}-${i}`} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3" data-testid={`acq-support-${i}`}>
              <span className="shrink-0 w-10 h-10 rounded-full bg-[#006090]/10 text-[#006090] font-semibold flex items-center justify-center text-sm">{(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate">{p.role}</p>
                <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                {p.email && <a href={`mailto:${p.email}`} className="text-xs text-[#006090] flex items-center gap-1 truncate hover:underline"><Mail className="w-3 h-3 shrink-0" />{p.email}</a>}
                {p.phone && <a href={`tel:${p.phone}`} className="text-xs text-slate-600 flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" />{p.phone}</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
